import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

/* ─────────────────────────────────────────────────────────────
   The plumbing under every camera on the dashboard: one shared clock,
   whether a feed is on screen, how many clips may decode at once, and
   full screen with a fallback for browsers that refuse it.

   Nothing here touches the DOM at import time, and every browser API
   is checked before use, so a test renderer without video, canvas or
   the Fullscreen API mounts the camera screens without complaint.
   ───────────────────────────────────────────────────────────── */

const hasWindow = typeof window !== 'undefined';

/* ── One clock for every timestamp ─────────────────────────────
   Sixteen overlays each running their own interval drift apart by a
   few hundred milliseconds, which on a wall of cameras reads as broken.
   They share one timer instead, aligned to the top of each second. */

const clockListeners = new Set();
let clockNow = Date.now();
let clockTimer = null;

function tickClock() {
    clockNow = Date.now();
    clockListeners.forEach(listener => listener());
    clockTimer = setTimeout(tickClock, 1000 - (clockNow % 1000) + 8);
}

function subscribeClock(listener) {
    clockListeners.add(listener);
    if (!clockTimer) {
        clockNow = Date.now();
        clockTimer = setTimeout(tickClock, 1000 - (clockNow % 1000) + 8);
    }
    return () => {
        clockListeners.delete(listener);
        if (!clockListeners.size) {
            clearTimeout(clockTimer);
            clockTimer = null;
        }
    };
}

const readClock = () => clockNow;

/* The current time in milliseconds, re-rendering once a second. */
export function useNow() {
    return useSyncExternalStore(subscribeClock, readClock, readClock);
}

/* ── Formatting ────────────────────────────────────────────── */

const pad = n => String(n).padStart(2, '0');

/* 13:42:05, the way a recorder stamps its frames. */
export function hms(t) {
    const d = new Date(t);
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function hm(t) {
    const d = new Date(t);
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function ymd(t) {
    const d = new Date(t);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function ago(t, now) {
    const s = Math.max(0, Math.round((now - t) / 1000));
    if (s < 45) return 'Just now';
    const m = Math.round(s / 60);
    if (m < 60) return `${m} min ago`;
    return `${Math.floor(m / 60)} h ago`;
}

/* A stable number from a camera id, for per-camera variety (drift
   timing, motion positions) that is the same on every render. */
export function seedOf(text) {
    let h = 2166136261;
    for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return Math.abs(h);
}

/* ── Playback position ─────────────────────────────────────────
   A camera that is not live carries { at, setAt, paused }: the moment
   on the recording the playhead was put at, the wall-clock time that
   happened, and whether it is held. Where the playhead is now follows
   from those, so nothing has to tick to keep a recording moving. */

export function headOf(playback, now) {
    if (!playback) return now;
    return playback.paused ? playback.at : playback.at + (now - playback.setAt);
}

/* `seek` marks a playhead that was moved, as opposed to one paused or
   resumed in place: only a move makes the clip jump. */
export const seekTo = at => ({ at, setAt: Date.now(), paused: false, seek: true });

/* ── Is this element on screen? ────────────────────────────────
   Where IntersectionObserver is missing altogether every feed counts as
   visible. Where it exists but never reports (the inert one the tests
   install) a feed stays a still, which is the cheap, safe answer. */

export function useInView(ref, rootMargin = '160px') {
    const [inView, setInView] = useState(() => hasWindow && typeof window.IntersectionObserver === 'undefined');

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        if (typeof window.IntersectionObserver === 'undefined') {
            setInView(true);
            return undefined;
        }
        const observer = new window.IntersectionObserver(entries => {
            const last = entries[entries.length - 1];
            if (last) setInView(last.isIntersecting);
        }, { rootMargin, threshold: 0.01 });
        observer.observe(el);
        return () => observer.disconnect();
    }, [ref, rootMargin]);

    return inView;
}

/* ── Is the page itself visible? ───────────────────────────── */

function subscribeVisibility(listener) {
    if (typeof document === 'undefined') return () => {};
    document.addEventListener('visibilitychange', listener);
    return () => document.removeEventListener('visibilitychange', listener);
}

const readVisibility = () => typeof document === 'undefined' || !document.hidden;

export function usePageVisible() {
    return useSyncExternalStore(subscribeVisibility, readVisibility, () => true);
}

/* ── How many clips decode at once ─────────────────────────────
   A nine-camera wall, its picker thumbnails and a viewer on top would
   otherwise ask a kiosk's GPU for a dozen decodes. Feeds that want to
   play queue for a slot; the ones past the budget show their still,
   which drifts and carries a live clock, so the wall still looks live. */

const slotListeners = new Set();
let slotQueue = [];
let slotSeq = 0;

function playBudget() {
    if (!hasWindow) return 0;
    const nav = window.navigator || {};
    if (nav.connection && nav.connection.saveData) return 2;
    /* The clips are 960px H.264, which any hardware decoder runs nine of.
       A phone gets fewer, and only the tiles scrolled into view ask. */
    const narrow = window.matchMedia ? window.matchMedia('(max-width: 760px)').matches : false;
    if (narrow) return 6;
    return (nav.hardwareConcurrency || 4) >= 4 ? 9 : 6;
}

function notifySlots() {
    slotListeners.forEach(listener => listener());
}

function subscribeSlots(listener) {
    slotListeners.add(listener);
    return () => slotListeners.delete(listener);
}

function claimSlot(token, priority) {
    slotQueue = slotQueue.filter(entry => entry.token !== token);
    slotQueue.push({ token, priority, order: ++slotSeq });
    slotQueue.sort((a, b) => b.priority - a.priority || a.order - b.order);
    notifySlots();
}

function releaseSlot(token) {
    slotQueue = slotQueue.filter(entry => entry.token !== token);
    notifySlots();
}

function holdsSlot(token) {
    const budget = playBudget();
    for (let i = 0; i < Math.min(budget, slotQueue.length); i++) {
        if (slotQueue[i].token === token) return true;
    }
    return false;
}

/* True while this feed may decode its clip. */
export function usePlaySlot(wanted, priority = 0) {
    const token = useRef(null);
    if (!token.current) token.current = {};

    useEffect(() => {
        if (!wanted) return undefined;
        const mine = token.current;
        claimSlot(mine, priority);
        return () => releaseSlot(mine);
    }, [wanted, priority]);

    return useSyncExternalStore(
        subscribeSlots,
        () => wanted && holdsSlot(token.current),
        () => false
    );
}

/* Clips that would not play are remembered for the session, so a tile
   that remounts (a layout change, a page turn) goes straight to its
   still instead of trying, and failing, again. */
const failedClips = new Set();
export const clipFailed = key => failedClips.has(key);
export const markClipFailed = key => { failedClips.add(key); };

/* ── Full screen ───────────────────────────────────────────────
   The whole document goes full screen, not the viewer element, so the
   overlays drawn into <body> (the viewer, a snapshot's modal, the
   on-screen keyboard) stay visible on top of it. Where the API is
   missing or refused (iOS Safari, an iframe without allowfullscreen)
   the viewer is still a fixed overlay over the kiosk: a "theater"
   that fills the window instead of the display. */

export function fullscreenElement() {
    if (typeof document === 'undefined') return null;
    return document.fullscreenElement || document.webkitFullscreenElement || null;
}

export function enterFullscreen() {
    if (typeof document === 'undefined') return;
    if (fullscreenElement()) return;
    const root = document.documentElement;
    const request = root.requestFullscreen || root.webkitRequestFullscreen;
    if (!request) return;
    try {
        const pending = request.call(root, { navigationUI: 'hide' });
        if (pending && pending.catch) pending.catch(() => {});
    } catch (error) {
        /* Refused: the overlay alone is the fallback. */
    }
}

export function exitFullscreen() {
    if (typeof document === 'undefined' || !fullscreenElement()) return;
    const exit = document.exitFullscreen || document.webkitExitFullscreen;
    if (!exit) return;
    try {
        const pending = exit.call(document);
        if (pending && pending.catch) pending.catch(() => {});
    } catch (error) {
        /* Already out. */
    }
}

/* Calls `onExit` when the browser leaves full screen by itself (the
   Escape key, a system gesture) while `active`, so the overlay closes
   with it rather than being left behind in a normal window. */
export function useFullscreenExit(active, onExit) {
    const callback = useRef(onExit);
    callback.current = onExit;

    useEffect(() => {
        if (!active || typeof document === 'undefined') return undefined;
        let entered = !!fullscreenElement();
        const onChange = () => {
            if (fullscreenElement()) entered = true;
            else if (entered) {
                entered = false;
                callback.current();
            }
        };
        document.addEventListener('fullscreenchange', onChange);
        document.addEventListener('webkitfullscreenchange', onChange);
        return () => {
            document.removeEventListener('fullscreenchange', onChange);
            document.removeEventListener('webkitfullscreenchange', onChange);
        };
    }, [active]);
}

/* True while another overlay (a modal, the keyboard) sits above the
   viewer, so a key meant for that overlay is not also acted on here. */
export function overlayAbove() {
    return typeof document !== 'undefined' && !!document.querySelector('.modal');
}
